'use client'

import { useEffect } from 'react'

import { Filter } from '@rewardops/api'
import { useFilter, useIsDesktop } from '@rewardops/ui'
import { ResponseItem } from '@rewardops/utils'

import { getCustomLocalisedSortOptionsByKey } from '~/utils/filter-utils'
import { RETAILERS_SORT_OPTIONS } from '~/utils/sort-utils'
import { useParametersFilter } from '~/components/hooks/filters/use-parameters-filter'
import { useEarnStatement } from '~/components/hooks/use-earn-statement'
import { usePaginatedItems } from '~/components/hooks/use-paginated-items'
import ResponsiveComponent from '~/components/responsive-component'
import {
  DEFAULT_DESKTOP_PAGE_SIZE,
  DEFAULT_MOBILE_PAGE_SIZE,
} from '~/app/common/constants'
import { SortOptionType, SortOrder } from '~/app/common/enums'
import DesktopRetailersSearch from './desktop-retailers-search'
import MobileRetailersSearch from './mobile-retailers-search'

export const DEFAULT_SORT_ORDER =
  `${SortOptionType.Name}_${SortOrder.Ascending}`.toLowerCase()

export type RetailersSearchProps = {
  items: ResponseItem[]
  itemsTotal: number
  searchQuery: string
  sort: any
  isItemsLoading: boolean
  isFiltersLoading?: boolean
  earnRatesFilterOptions?: any[]
  itemSearchState?: { totalItems: number | undefined; loading: boolean }
  setItemSearchState?: React.Dispatch<
    React.SetStateAction<{ totalItems: number | undefined; loading: boolean }>
  >
}

const RetailersSearch = () => {
  const {
    searchQuery,
    sort,
    selectedEarnRatesExpressions,
    currentPage,
    itemsPerPage,
    setPageInfo,
  } = useFilter()

  const { mapEarnRateOptions } = useEarnStatement()

  const isDesktop = useIsDesktop()

  const filter: Filter = {
    sort: getCustomLocalisedSortOptionsByKey(sort, RETAILERS_SORT_OPTIONS),
    isAffiliate: true,
    earnRateCashExpressions: selectedEarnRatesExpressions(),
    ...(searchQuery != null ? { q: searchQuery } : {}),
  }

  const { loading, items, totalItems } = usePaginatedItems(
    filter,
    currentPage,
    itemsPerPage,
  )

  const { earnRates: earnRatesOptions, loading: isLoadingFiltersData } =
    useParametersFilter({
      earnRate: 'raw',
      searchQuery: searchQuery,
      enabled: true,
    })

  useEffect(() => {
    if (!isDesktop) {
      setPageInfo(1, DEFAULT_MOBILE_PAGE_SIZE, DEFAULT_SORT_ORDER)
    } else {
      setPageInfo(1, DEFAULT_DESKTOP_PAGE_SIZE, DEFAULT_SORT_ORDER)
    }
  }, [])

  const desktopComponent = (
    <DesktopRetailersSearch
      items={items}
      sort={sort}
      itemsTotal={totalItems ?? 0}
      searchQuery={searchQuery}
      earnRatesFilterOptions={mapEarnRateOptions(earnRatesOptions)}
      isItemsLoading={loading}
      isFiltersLoading={isLoadingFiltersData}
    />
  )

  const mobileComponent = (
    <MobileRetailersSearch
      items={items}
      itemsTotal={totalItems ?? 0}
      searchQuery={searchQuery}
      sort={sort}
      earnRatesFilterOptions={mapEarnRateOptions(earnRatesOptions)}
      isItemsLoading={loading}
      isFiltersLoading={isLoadingFiltersData}
    />
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export default RetailersSearch
